import * as React from 'react';
import { useState } from 'react';
import { Outlet, useNavigate } from 'react-router';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';
import {
    Avatar,
    Box,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Tooltip,
    Typography,
    useColorScheme,
} from "@mui/material";
import PropTypes from "prop-types";
import {
    AccountCircleRounded,
    BusinessRounded,
    CorporateFareRounded,
    DarkMode,
    LightMode,
    LogoutRounded,
    PersonRounded,
    SettingsRounded,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import PageAuthGate from "./PageAuthGate";
import SelectCorproate from "./SelectCorproate";
import LogoutIcon from "../../pages/auth/LogoutIcon";
import ChatWidget from "./ChatWidget";

export default function Layout() {
    const { mode, setMode } = useColorScheme();
    const navigate = useNavigate();
    const currentUser = useSelector(state => state.auth.user);

    const [userMenuAnchor, setUserMenuAnchor] = useState(null);
    const [logoutOpen, setLogoutOpen] = useState(false);

    if (!mode) {
        return null;
    }

    const handleOpenUserMenu = (event) => {
        setUserMenuAnchor(event.currentTarget);
    };

    const handleCloseUserMenu = () => {
        setUserMenuAnchor(null);
    };

    function SidebarFooter({ mini }) {
        return mini ? (
            <Box sx={{ textAlign: 'center', py: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: 'primary.main', fontSize: '0.75rem' }}>
                    جیم‌پین
                </Typography>
            </Box>
        ) : (
            <Box
                sx={{
                    px: 2,
                    py: 1.5,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                }}
            >
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.74rem' }}>
                    جیم‌پین سازمانی © {new Date().getFullYear()}
                </Typography>
                <LogoutIcon />
            </Box>
        );
    }

    function ToolbarActions() {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                {/* انتخاب سازمان فعال */}
                <SelectCorproate />

                {/* دکمه تغییر تم لایت و دارک */}
                <Tooltip title={mode === 'light' ? "تغییر به تم تاریک" : "تغییر به تم روشن"}>
                    <IconButton
                        size="small"
                        onClick={() => setMode(mode === 'light' ? 'dark' : 'light')}
                        sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2.5,
                            p: 0.8,
                            '&:hover': { bgcolor: 'action.hover' },
                        }}
                    >
                        {mode === 'light' ? (
                            <DarkMode sx={{ fontSize: 20, color: 'text.secondary' }} />
                        ) : (
                            <LightMode sx={{ fontSize: 20, color: '#f59e0b' }} />
                        )}
                    </IconButton>
                </Tooltip>

                {/* آیکون پروفایل کاربری مدیر و منوی سریع */}
                <Tooltip title="پروفایل مدیر و تنظیمات">
                    <IconButton
                        size="small"
                        onClick={handleOpenUserMenu}
                        sx={{
                            p: 0.3,
                            border: '2px solid',
                            borderColor: 'primary.light',
                            borderRadius: '50%',
                        }}
                    >
                        <Avatar
                            src={currentUser?.Avatar?.Url}
                            alt={currentUser?.FullName || "Manager"}
                            sx={{
                                width: 32,
                                height: 32,
                                bgcolor: 'primary.main',
                                color: '#fff',
                                fontSize: '0.85rem',
                                fontWeight: 700,
                            }}
                        >
                            {currentUser?.FullName ? currentUser.FullName.slice(0, 1) : <PersonRounded sx={{ fontSize: 18 }} />}
                        </Avatar>
                    </IconButton>
                </Tooltip>

                {/* منوی دراپ‌داون پروفایل کاربر */}
                <Menu
                    anchorEl={userMenuAnchor}
                    open={Boolean(userMenuAnchor)}
                    onClose={handleCloseUserMenu}
                    PaperProps={{
                        sx: {
                            minWidth: 220,
                            borderRadius: 3.5,
                            p: 1,
                            mt: 1.2,
                            boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
                        },
                    }}
                    transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                >
                    <Box sx={{ px: 2, py: 1.2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                            {currentUser?.FullName || "مدیر سازمان"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {currentUser?.PhoneNumber || ""}
                        </Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <MenuItem
                        onClick={() => {
                            handleCloseUserMenu();
                            navigate('/settings');
                        }}
                        sx={{ borderRadius: 2 }}
                    >
                        <ListItemIcon>
                            <AccountCircleRounded fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="پروفایل کاربری" primaryTypographyProps={{ fontSize: '0.88rem', fontWeight: 600 }} />
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            handleCloseUserMenu();
                            navigate('/settings/corporates');
                        }}
                        sx={{ borderRadius: 2 }}
                    >
                        <ListItemIcon>
                            <CorporateFareRounded fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="سازمان‌های من" primaryTypographyProps={{ fontSize: '0.88rem', fontWeight: 600 }} />
                    </MenuItem>
                    <Divider sx={{ my: 1 }} />
                    <MenuItem
                        onClick={() => {
                            handleCloseUserMenu();
                            navigate('/auth/logout');
                        }}
                        sx={{ borderRadius: 2, color: 'error.main' }}
                    >
                        <ListItemIcon>
                            <LogoutRounded fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText primary="خروج از حساب" primaryTypographyProps={{ fontSize: '0.88rem', fontWeight: 700 }} />
                    </MenuItem>
                </Menu>
            </Box>
        );
    }

    SidebarFooter.propTypes = {
        mini: PropTypes.bool.isRequired,
    };

    return (
        <DashboardLayout
            slots={{
                sidebarFooter: SidebarFooter,
                toolbarActions: ToolbarActions,
            }}
        >
            <PageAuthGate>
                <Outlet />
                <ChatWidget />
            </PageAuthGate>
        </DashboardLayout>
    );
}
